import { tenantApi } from '@shared/api/tenantApi';
import { unwrapList } from '@shared/api/unwrap';

/**
 * Member utilities — handler-driven dynamic actions per device.
 *
 * Utilities are now derived directly from devices: any device carrying a
 * `utility_type_id` at a property the member has an approved claim on surfaces
 * here. There is no per-member linking step. The `link_id` returned below IS
 * the device id and is what action routing keys off.
 *
 * Endpoints used:
 *   - `GET  /members/me/utilities/types`              utility-type catalogue
 *   - `GET  /members/me/utility-links`                this member's utilities (device-derived)
 *   - `GET  /members/me/utility-links/:linkId/actions`             actions advertised by the handler
 *   - `POST /members/me/utility-links/:linkId/actions/:actionKey`  invoke an action
 */

export interface UtilityTypeOption {
  utility_type_id: string;
  name: string;
  description?: string;
  is_active: boolean;
}

export interface UtilityActivationPair {
  utility_type_id: string;
  device_type_id: string;
  is_active: boolean;
}

export interface UtilityIdentifierHint {
  device_type_id: string;
  device_type_name: string;
  handler_key: string;
  identifier_schema: Record<string, unknown> | null;
}

export interface MyUtilityLink {
  link_id: string;
  utility_type_id: string;
  location_id: string;
  device_id: string | null;
  identifier_payload: Record<string, unknown> | null;
  status: string;
  created_at: string;
}

export type UtilityActionCategory = 'read' | 'write' | 'admin';
export type UtilityActionRenderHint = 'chart' | 'list' | 'button' | 'gauge' | 'iframe' | 'form';

export interface UtilityAction {
  action_key: string;
  label: string;
  description?: string;
  category: UtilityActionCategory;
  render_hint: UtilityActionRenderHint;
  member_invocable: boolean;
  display_order?: number;
  parameters_schema?: Record<string, unknown>;
  result_schema?: Record<string, unknown>;
}

export interface UtilityActionResult {
  success: boolean;
  data?: unknown;
  message?: string;
  metadata?: Record<string, unknown>;
}

/** Result shape for the Ontec `get_usage` action — used to type the chart. */
export interface UsageSeriesPoint {
  at: string;
  register_code: string;
  value: number;
  delta: number | null;
  is_estimate?: boolean;
  is_manual?: boolean;
}

export interface UsageActionData {
  series: UsageSeriesPoint[];
  registers: string[];
  total_consumption: number;
  unit?: string | null;
  from: string;
  to: string;
  primary_register?: string | null;
  filtered_registers?: string[];
  /** Friendly labels for the registers in this response (code → label). */
  register_labels?: Record<string, string>;
}

/** Result shape for the Urbtec `buy_token` action - the hosted checkout to hand off to. */
export interface StartPurchaseData {
  vend_id: string;
  payment_link: string;
  amount_cents: number;
}

/** One row of the Urbtec `purchase_history` action. */
export interface VendSummary {
  vend_id: string;
  meter_num: string;
  amount_cents: number;
  currency_code: string;
  /** awaiting_payment | paid | vended | vend_failed | cancelled */
  state: string;
  /** The STS token to key into the meter. Present once `state` is `vended`. */
  token: string | null;
  quantity: number | null;
  quantity_unit: string | null;
  message: string | null;
  created_at: string;
  vended_at: string | null;
}

export interface PurchaseHistoryData {
  purchases: VendSummary[];
}

/* ── Fetchers ──────────────────────────────────────────────────── */

export async function fetchMyUtilityTypes(): Promise<UtilityTypeOption[]> {
  const { data } = await tenantApi.get('/members/me/utilities/types');
  return unwrapList<UtilityTypeOption>(data);
}

export async function fetchMyUtilityActivation(): Promise<UtilityActivationPair[]> {
  const { data } = await tenantApi.get('/members/me/utilities/activation');
  return unwrapList<UtilityActivationPair>(data);
}

export async function fetchMyUtilityIdentifierHints(): Promise<UtilityIdentifierHint[]> {
  const { data } = await tenantApi.get('/members/me/utilities/identifier-hints');
  return unwrapList<UtilityIdentifierHint>(data);
}

export async function fetchMyUtilityLinks(): Promise<MyUtilityLink[]> {
  const { data } = await tenantApi.get('/members/me/utility-links');
  return unwrapList<MyUtilityLink>(data);
}

export async function fetchLinkActions(linkId: string): Promise<UtilityAction[]> {
  const { data } = await tenantApi.get(`/members/me/utility-links/${linkId}/actions`);
  return unwrapList<UtilityAction>(data);
}

export async function invokeLinkAction<T = unknown>(
  linkId: string,
  actionKey: string,
  parameters: Record<string, unknown> = {},
): Promise<UtilityActionResult & { data?: T }> {
  const { data } = await tenantApi.post(
    `/members/me/utility-links/${linkId}/actions/${actionKey}`,
    parameters,
  );
  return data as UtilityActionResult & { data?: T };
}
